import React, { useState, useMemo, useCallback } from 'react';
import { 
  UserX, 
  Search, 
  RefreshCw, 
  ExternalLink, 
  Image as ImageIcon,
  Globe,
  ShoppingBag,
  AlertTriangle,
  Share2,
  DollarSign,
  Users,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

interface ImpersonationCase {
  id: number;
  platform: string;
  type: string;
  risk: 'high' | 'medium' | 'low';
  status: 'investigating' | 'reported' | 'takedown';
  sourceLink: string;
  screenshot: string;
  websiteType: string;
  detectedAt: string;
  lastChecked: string;
  followers?: number;
  engagement?: number;
  description: string;
  similarityScore: number;
  products?: number;
  revenue?: string;
  location?: string;
  registrationDate?: string;
  associatedDomains?: string[];
  socialLinks?: { platform: string; url: string; }[];
  violations?: string[];
  brandAssets?: { type: string; url: string; }[];
}

const mockCases: ImpersonationCase[] = [
  {
    id: 1,
    platform: 'Shopee MY',
    type: 'Seller',
    risk: 'high',
    status: 'investigating',
    sourceLink: 'https://shopee.com.my/nike.official.fake',
    screenshot: '/screenshots/shopee-fake-1.jpg',
    websiteType: 'E-commerce',
    detectedAt: '2025-02-01T14:23:00Z',
    lastChecked: '2025-02-02T10:15:00Z',
    followers: 1243,
    engagement: 89,
    description: 'Unauthorized seller using brand assets and claiming to be official store',
    similarityScore: 92,
    products: 156,
    revenue: '$45,000',
    location: 'Kuala Lumpur, Malaysia',
    registrationDate: '2024-12-15',
    associatedDomains: [
      'brandoutlet.com.my',
      'branddeals.my'
    ],
    socialLinks: [
      { platform: 'Instagram', url: 'https://instagram.com/brand.deals.my' },
      { platform: 'Facebook', url: 'https://facebook.com/branddealsmy' }
    ],
    violations: [
      'Unauthorized use of brand logo',
      'False claims of authenticity',
      'Counterfeit product sales'
    ],
    brandAssets: [
      { type: 'Logo', url: '/brand-assets/logo-misuse-1.jpg' },
      { type: 'Banner', url: '/brand-assets/banner-misuse-1.jpg' }
    ]
  },
  {
    id: 1,
    platform: 'Shopee MY',
    type: 'Seller',
    risk: 'high',
    status: 'investigating',
    sourceLink: 'https://shopee.com.my/nike.official.fake',
    screenshot: '/screenshots/shopee-fake-1.jpg',
    websiteType: 'E-commerce',
    detectedAt: '2025-02-01T14:23:00Z',
    lastChecked: '2025-02-02T10:15:00Z',
    followers: 1243,
    engagement: 89,
    description: 'Unauthorized seller using brand assets and claiming to be official store',
    similarityScore: 92
  },
  {
    id: 2,
    platform: 'Lazada',
    type: 'Store',
    risk: 'medium',
    status: 'reported',
    sourceLink: 'https://lazada.com.my/store/brand-outlet-deals',
    screenshot: '/screenshots/lazada-fake-1.jpg',
    websiteType: 'E-commerce',
    detectedAt: '2025-02-01T08:45:00Z',
    lastChecked: '2025-02-02T09:30:00Z',
    followers: 856,
    engagement: 45,
    description: 'Store using similar branding and selling counterfeit products',
    similarityScore: 78,
    products: 89,
    revenue: '$23,000',
    location: 'Penang, Malaysia',
    registrationDate: '2025-01-05',
    associatedDomains: [
      'brandoutletmy.com'
    ],
    socialLinks: [
      { platform: 'TikTok', url: 'https://tiktok.com/@brandoutlet.my' }
    ],
    violations: [
      'Similar brand name usage',
      'Counterfeit product listings',
      'Misleading product descriptions'
    ],
    brandAssets: [
      { type: 'Product Images', url: '/brand-assets/product-misuse-1.jpg' }
    ]
  },
  {
    id: 3,
    platform: 'TikTok',
    type: 'Profile',
    risk: 'high',
    status: 'takedown',
    sourceLink: 'https://tiktok.com/@brand.official.store',
    screenshot: '/screenshots/tiktok-fake-1.jpg',
    websiteType: 'Social Media',
    detectedAt: '2025-02-01T19:10:00Z',
    lastChecked: '2025-02-02T11:20:00Z',
    followers: 15600,
    engagement: 234,
    description: 'Account impersonating official brand and promoting suspicious links',
    similarityScore: 95,
    location: 'Singapore',
    registrationDate: '2025-01-20',
    associatedDomains: [
      'brandsale.shop',
      'branddeals.store'
    ],
    socialLinks: [
      { platform: 'Instagram', url: 'https://instagram.com/brand.deals.official' },
      { platform: 'Telegram', url: 'https://t.me/branddeals_official' }
    ],
    violations: [
      'Impersonation of official account',
      'Unauthorized use of brand content',
      'Phishing link distribution',
      'False claims of partnership'
    ],
    brandAssets: [
      { type: 'Profile Picture', url: '/brand-assets/profile-misuse-1.jpg' },
      { type: 'Video Content', url: '/brand-assets/video-misuse-1.mp4' }
    ]
  }
];

const BrandImpersonation: React.FC = () => {
  const [selectedCase, setSelectedCase] = useState<ImpersonationCase | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter cases based on search query
  const filteredCases = useMemo(() => {
    if (!searchQuery) return mockCases;
    
    const query = searchQuery.toLowerCase();
    return mockCases.filter(case_ =>
      case_.platform.toLowerCase().includes(query) ||
      case_.type.toLowerCase().includes(query) ||
      case_.description.toLowerCase().includes(query) ||
      case_.status.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Refresh cases data
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // In production, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Simulating refresh success
      setIsRefreshing(false);
    } catch (error) {
      console.error('Error refreshing cases:', error);
      setIsRefreshing(false);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <UserX className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Brand Impersonation</h1>
            <p className="text-sm text-gray-400">Monitor and prevent brand impersonation attempts</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add a new case
          </Button>
        </div>
      </div>

      {/* Active Cases */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-white">Active Cases</h2>
            <span className="px-2 py-1 text-xs rounded-full bg-blue-500/10 text-blue-500">
              {filteredCases.length} of {mockCases.length} cases
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 text-sm bg-gray-800/30 border border-gray-700/50 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent w-64"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredCases.map((case_) => (
            <div 
              key={case_.id} 
              className={`flex flex-col p-4 rounded-lg transition-all duration-200 ${selectedCase?.id === case_.id ? 'bg-gray-800/50 ring-1 ring-blue-500/50' : 'bg-gray-800/30 hover:bg-gray-800/40'} cursor-pointer`}
              onClick={() => setSelectedCase(selectedCase?.id === case_.id ? null : case_)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${case_.risk === 'high' ? 'bg-red-500/10' : case_.risk === 'medium' ? 'bg-yellow-500/10' : 'bg-green-500/10'}`}>
                      {case_.websiteType === 'E-commerce' ? (
                        <ShoppingBag className={`h-5 w-5 ${case_.risk === 'high' ? 'text-red-500' : case_.risk === 'medium' ? 'text-yellow-500' : 'text-green-500'}`} />
                      ) : (
                        <Globe className={`h-5 w-5 ${case_.risk === 'high' ? 'text-red-500' : case_.risk === 'medium' ? 'text-yellow-500' : 'text-green-500'}`} />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-white">{case_.platform}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        case_.risk === 'high' ? 'bg-red-500/10 text-red-500' : 
                        case_.risk === 'medium' ? 'bg-yellow-500/10 text-yellow-500' : 
                        'bg-green-500/10 text-green-500'
                      }`}>
                        {case_.risk.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 flex items-center space-x-2">
                      <span>{case_.type}</span>
                      <span>•</span>
                      <span>Detected {new Date(case_.detectedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Similarity Score</div>
                    <div className="text-sm font-medium text-white">{case_.similarityScore}%</div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    case_.status === 'takedown' ? 'bg-purple-500/10 text-purple-500' :
                    case_.status === 'investigating' ? 'bg-yellow-500/10 text-yellow-500' :
                    'bg-blue-500/10 text-blue-500'
                  }`}>
                    {case_.status.toUpperCase()}
                  </span>
                  <Button variant="outline" size="sm">
                    <AlertTriangle className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedCase?.id === case_.id && (
                <div className="mt-4 pt-4 border-t border-gray-800/50 space-y-4">
                  {/* Source and Screenshot */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <div className="text-sm text-gray-400 mb-1">Source</div>
                      <a 
                        href={case_.sourceLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-400 hover:text-blue-300 flex items-center"
                      >
                        {case_.sourceLink}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-400 mb-1">Screenshot</div>
                      <div className="bg-gray-900/50 rounded p-2 inline-flex items-center space-x-2">
                        <ImageIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-500">View Screenshot</span>
                      </div>
                    </div>
                  </div>

                  {/* Key Info */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-400 mb-1 flex items-center">
                        <Globe className="h-4 w-4 mr-1" />
                        Website Type
                      </div>
                      <div className="text-sm text-white">{case_.websiteType}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1 flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Similarity Score
                      </div>
                      <div className="text-sm text-white">{case_.similarityScore}%</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1 flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        Followers
                      </div>
                      <div className="text-sm text-white">{case_.followers?.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="grid grid-cols-3 gap-4">
                    {case_.products && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1 flex items-center">
                          <ShoppingBag className="h-4 w-4 mr-1" />
                          Products
                        </div>
                        <div className="text-sm text-white">{case_.products}</div>
                      </div>
                    )}
                    {case_.revenue && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1 flex items-center">
                          <DollarSign className="h-4 w-4 mr-1" />
                          Est. Revenue
                        </div>
                        <div className="text-sm text-white">{case_.revenue}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm text-gray-400 mb-1 flex items-center">
                        <Share2 className="h-4 w-4 mr-1" />
                        Engagement
                      </div>
                      <div className="text-sm text-white">{case_.engagement} per day</div>
                    </div>
                  </div>

                  {/* Location and Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    {case_.location && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Location</div>
                        <div className="text-sm text-white">{case_.location}</div>
                      </div>
                    )}
                    {case_.registrationDate && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Registration Date</div>
                        <div className="text-sm text-white">
                          {new Date(case_.registrationDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Violations */}
                  {case_.violations && case_.violations.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-400 mb-2">Violations</div>
                      <div className="space-y-1">
                        {case_.violations.map((violation, index) => (
                          <div 
                            key={index} 
                            className="text-sm text-white bg-red-500/10 px-3 py-1.5 rounded-md"
                          >
                            {violation}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Associated Domains */}
                  {case_.associatedDomains && case_.associatedDomains.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-400 mb-2">Associated Domains</div>
                      <div className="flex flex-wrap gap-2">
                        {case_.associatedDomains.map((domain, index) => (
                          <div 
                            key={index} 
                            className="text-sm text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-md flex items-center"
                          >
                            <Globe className="h-3 w-3 mr-1" />
                            {domain}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Social Links */}
                  {case_.socialLinks && case_.socialLinks.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-400 mb-2">Social Links</div>
                      <div className="flex flex-wrap gap-2">
                        {case_.socialLinks.map((link, index) => (
                          <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-400 bg-purple-500/10 px-3 py-1.5 rounded-md flex items-center hover:bg-purple-500/20 transition-colors"
                          >
                            <Share2 className="h-3 w-3 mr-1" />
                            {link.platform}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Description</div>
                    <div className="text-sm text-white bg-gray-800/50 p-3 rounded-md">{case_.description}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandImpersonation;